import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  referralApi,
  referralStatusLabel,
  type ReferralItemOut,
  type ReferralMeOut,
  type ReferralStatsOut
} from '@/api/referral';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Eyebrow } from '@/components/ui/Typography';
import { AURORA_ACCENT, AuroraCard } from '@/components/referral/AuroraCard';
import { REFERRAL_TEXT } from '@/components/referral/ReferralCard';
import { ArrowLeftLine, GiftLine, PeopleLine, WhatsGlyph } from '@/components/referral/icons';
import { formatBRL } from '@/lib/format';
import { friendlyMessage } from '@/lib/errors';
import {
  canNativeShare,
  copyText,
  inviteMessage,
  shareInvite,
  whatsappShareUrl
} from '@/lib/referral';

/**
 * Tela do programa Indique e Ganhe (autenticada, dentro do AppLayout) —
 * design "Bow Perfil e Indique" (Claude Design · bow-perfil-indique.jsx ·
 * IndiqueScreen): header com voltar, card aurora com a regra, card do convite
 * (código + QR + compartilhar/copiar), 3 números e "Seus indicados".
 *
 * Valores vêm do backend (regra versionada) — nada hardcodado. Os blocos
 * visuais são componentes PUROS (props) pros smoke tests SSR.
 */

const DISPLAY = 'Chillax, system-ui, sans-serif';

export default function Indique() {
  const navigate = useNavigate();
  const [me, setMe] = useState<ReferralMeOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [showHow, setShowHow] = useState(false);

  useEffect(() => {
    referralApi
      .me()
      .then(setMe)
      .catch((err) => setError(friendlyMessage(err, 'Não foi possível carregar')));
  }, []);

  function flash(kind: 'code' | 'link') {
    setCopied(kind);
    window.setTimeout(() => setCopied(null), 1500);
  }
  async function copyCode() {
    if (me?.code && (await copyText(me.code))) flash('code');
  }
  async function copyLink() {
    if (me?.link && (await copyText(me.link))) flash('link');
  }
  const message =
    me?.code && me.link
      ? inviteMessage({ code: me.code, link: me.link, welcomeBrl: me.welcome_brl })
      : null;
  function shareNow() {
    if (!message) return;
    void shareInvite(message).then((outcome) => {
      if (outcome === 'copied') flash('link');
    });
  }
  function goBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate('/profile');
  }

  return (
    <div className="flex flex-col gap-[17px]">
      <IndiqueHeader onBack={goBack} />

      {error && (
        <Card>
          <p className="text-sm text-bow-red">{error}</p>
        </Card>
      )}

      {!me && !error && (
        <Card className="flex flex-col gap-3">
          <div className="h-3 w-28 animate-pulse rounded bg-bow-royal/10" />
          <div className="h-10 w-44 animate-pulse rounded bg-bow-royal/10" />
          <div className="h-40 w-40 animate-pulse self-center rounded-2xl bg-bow-royal/10" />
        </Card>
      )}

      {me && !me.enabled && (
        <Card>
          <p className="text-sm leading-relaxed text-bow-royal/75">
            O programa de indicação não está disponível no momento. Assim que voltar, seu
            código aparece aqui.
          </p>
        </Card>
      )}

      {me && me.enabled && me.code && me.link && (
        <>
          <IndiqueHero
            percent={me.referrer_percent}
            windowDays={me.window_days}
            welcomeBrl={me.welcome_brl}
            onHowItWorks={() => setShowHow(true)}
          />

          <InviteCard
            code={me.code}
            link={me.link}
            copied={copied}
            shareHref={canNativeShare() || !message ? null : whatsappShareUrl(message)}
            onCopyCode={() => void copyCode()}
            onCopyLink={() => void copyLink()}
            onShare={shareNow}
          />

          <IndiqueStats stats={me.stats} />

          <IndicadosCard referrals={me.referrals} />

          {showHow && (
            <HowItWorksSheet
              percent={me.referrer_percent}
              windowDays={me.window_days}
              welcomeBrl={me.welcome_brl}
              inactiveDays={me.inactive_days}
              onClose={() => setShowHow(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ───────────────────────── blocos puros ───────────────────────── */

export function IndiqueHeader({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex items-center gap-4 pt-1">
      <button
        type="button"
        onClick={onBack}
        aria-label="Voltar"
        className="inline-flex text-bow-royal"
      >
        <ArrowLeftLine size={25} />
      </button>
      <span
        className="text-bow-royal"
        style={{ fontFamily: DISPLAY, fontSize: 21, fontWeight: 500, letterSpacing: '-0.02em' }}
      >
        Indique e ganhe
      </span>
    </div>
  );
}

/** Card aurora com a regra do programa. */
export function IndiqueHero({
  percent,
  windowDays,
  welcomeBrl,
  onHowItWorks
}: {
  percent: string;
  windowDays: number;
  welcomeBrl: string;
  onHowItWorks: () => void;
}) {
  return (
    <AuroraCard padding="20px 20px 22px">
      <div className="flex items-start gap-4">
        <GiftLine size={66} />
        <div className="min-w-0 flex-1 pt-0.5">
          <div
            className="whitespace-nowrap"
            style={{
              fontFamily: DISPLAY,
              fontWeight: 600,
              fontSize: 27,
              letterSpacing: '-0.03em',
              lineHeight: 1.02
            }}
          >
            {percent}% de cashback
          </div>
          <div className="mt-[3px] text-[16px]" style={{ color: 'rgba(241,237,228,0.84)' }}>
            por {windowDays} dias
          </div>
          <div className="mt-[5px] text-[15.5px] font-semibold" style={{ color: AURORA_ACCENT }}>
            Seu amigo ganha {formatBRL(welcomeBrl).replace(',00', '')}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onHowItWorks}
        className="mt-3.5 text-[14px] font-semibold text-bow-cream underline underline-offset-[3px]"
      >
        Como funciona
      </button>
    </AuroraCard>
  );
}

/** Código + QR + compartilhar/copiar. */
export function InviteCard({
  code,
  link,
  copied,
  shareHref,
  onCopyCode,
  onCopyLink,
  onShare
}: {
  code: string;
  link: string;
  copied: 'code' | 'link' | null;
  /** Âncora WhatsApp quando não há share nativo; null → `onShare`. */
  shareHref: string | null;
  onCopyCode: () => void;
  onCopyLink: () => void;
  onShare: () => void;
}) {
  return (
    <Card className="p-5 pb-[22px]">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <Eyebrow>Seu convite</Eyebrow>
          <div
            className="mt-[5px] truncate text-bow-royal"
            style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: 25, letterSpacing: '0.10em' }}
          >
            {code}
          </div>
        </div>
        <button
          type="button"
          onClick={onCopyCode}
          className="h-[42px] shrink-0 rounded-full border-[1.5px] border-bow-royal/15 bg-white px-[22px] text-[14px] font-semibold text-bow-royal"
        >
          {copied === 'code' ? 'Copiado!' : 'Copiar'}
        </button>
      </div>

      <div className="mt-[18px] flex justify-center">
        <div
          className="rounded-2xl border border-bow-royal/[0.07] bg-white p-3"
          style={{ lineHeight: 0 }}
        >
          <QRCodeSVG value={link} size={192} bgColor="#FFFFFF" fgColor="#001B74" level="M" />
        </div>
      </div>
      <div className="mt-[13px] text-center text-[14px]" style={{ color: 'rgba(0,27,116,0.52)' }}>
        Mostre o QR ou compartilhe
      </div>

      <div className="mt-[15px] flex flex-col gap-2.5">
        {shareHref ? (
          <a href={shareHref} target="_blank" rel="noreferrer" className="block no-underline">
            <Button variant="accent" size="lg" type="button" className="h-[52px] text-[15.5px]">
              <WhatsGlyph size={20} /> Compartilhar convite
            </Button>
          </a>
        ) : (
          <Button
            variant="accent"
            size="lg"
            type="button"
            onClick={onShare}
            className="h-[52px] text-[15.5px]"
          >
            <WhatsGlyph size={20} /> Compartilhar convite
          </Button>
        )}
        <button
          type="button"
          onClick={onCopyLink}
          className="h-[52px] rounded-full border-[1.5px] border-bow-royal/[0.14] bg-white text-[15.5px] font-semibold text-bow-royal"
        >
          {copied === 'link' ? 'Link copiado!' : 'Copiar link'}
        </button>
      </div>
    </Card>
  );
}

/** Indicados · Rendendo · Ganhos. */
export function IndiqueStats({ stats }: { stats: ReferralStatsOut }) {
  const cells: Array<[string, string, string]> = [
    [String(stats.claimed), 'Indicados', '#001B74'],
    [String(stats.earning), 'Rendendo', '#001B74'],
    [formatBRL(stats.earned_brl), 'Ganhos', REFERRAL_TEXT]
  ];
  return (
    <Card className="px-3 py-[19px]">
      <div className="grid grid-cols-3">
        {cells.map(([value, label, color], i) => (
          <div
            key={label}
            className="text-center"
            style={{ borderLeft: i ? '1px solid rgba(0,27,116,0.10)' : 'none' }}
          >
            <div
              className="truncate px-1"
              style={{
                fontFamily: DISPLAY,
                fontWeight: 600,
                fontSize: 26,
                letterSpacing: '-0.03em',
                lineHeight: 1,
                color
              }}
            >
              {value}
            </div>
            <div className="mt-1.5 text-[13px]" style={{ color: 'rgba(0,27,116,0.52)' }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** "Seus indicados" — vazio (design) ou lista com etapa e quanto rendeu. */
export function IndicadosCard({ referrals }: { referrals: ReferralItemOut[] }) {
  const count = referrals.length;
  return (
    <Card className="p-5">
      <div className="flex items-center gap-4">
        <PeopleLine size={56} />
        <div className="min-w-0 flex-1">
          <div className="text-[15.5px] font-semibold tracking-[-0.01em] text-bow-royal">
            Seus indicados
          </div>
          <div
            className="mt-[3px]"
            style={{
              fontFamily: DISPLAY,
              fontWeight: 500,
              fontSize: 19,
              letterSpacing: '-0.02em',
              color: 'rgba(0,27,116,0.45)'
            }}
          >
            {count === 0 ? 'Nenhum amigo ainda' : `${count} ${count === 1 ? 'amigo' : 'amigos'}`}
          </div>
          {count === 0 && (
            <div className="mt-[3px] text-[12.5px]" style={{ color: 'rgba(0,27,116,0.52)' }}>
              Compartilhe seu convite para começar.
            </div>
          )}
        </div>
      </div>
      {count > 0 && (
        <ul className="mt-3 flex flex-col divide-y divide-bow-royal/5 border-t border-bow-royal/5">
          {referrals.map((r, i) => (
            <li
              key={`${r.first_name}-${r.claimed_at}-${i}`}
              className="flex items-center justify-between gap-3 py-3"
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-[15px] font-semibold text-bow-royal">
                  {r.first_name}
                </span>
                <span className="text-[12px] text-bow-royal/55">{referralStatusLabel(r)}</span>
              </div>
              <span
                className="shrink-0 text-[14px] font-semibold"
                style={{
                  color: Number(r.earned_brl) > 0 ? REFERRAL_TEXT : 'rgba(0,27,116,0.4)'
                }}
              >
                {Number(r.earned_brl) > 0 ? `+${formatBRL(r.earned_brl)}` : '—'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function HowItWorksSheet({
  percent,
  windowDays,
  welcomeBrl,
  inactiveDays,
  onClose
}: {
  percent: string;
  windowDays: number;
  welcomeBrl: string;
  inactiveDays: number;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-bow-royal/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[88vh] w-full max-w-md flex-col rounded-t-3xl bg-bow-cream sm:mb-8 sm:rounded-3xl">
        <div className="border-b border-bow-royal/10 px-6 py-4">
          <Eyebrow>Como funciona</Eyebrow>
          <p className="mt-1 text-lg font-semibold text-bow-royal">Indique e ganhe</p>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5 text-sm leading-relaxed text-bow-royal/80">
          <div>
            <span className="font-semibold text-bow-royal">
              Seu amigo ganha {formatBRL(welcomeBrl)}.
            </span>{' '}
            Ele aplica seu código (pelo link, pelo QR ou no cadastro) e, no primeiro aporte de
            saldo de pelo menos {formatBRL(welcomeBrl)} (PIX ou cartão), recebe{' '}
            {formatBRL(welcomeBrl)} de cashback pra usar na recarga.
          </div>
          <div>
            <span className="font-semibold text-bow-royal">
              Você ganha {percent}% por {windowDays} dias.
            </span>{' '}
            A partir da primeira recarga dele paga com saldo, cada recarga dele paga com saldo
            rende {percent}% em cashback pra você, durante {windowDays} dias — e você recebe uma
            notificação a cada vez que render. Recarga paga com cashback não conta.
          </div>
          <div>
            <span className="font-semibold text-bow-royal">Vale pra quem está começando.</span>{' '}
            O convite é pra quem nunca carregou na Bow ou não carrega há mais de {inactiveDays}{' '}
            dias. Cada pessoa pode usar um código uma única vez.
          </div>
          <div>
            <span className="font-semibold text-bow-royal">Tudo em cashback.</span> O bônus cai na
            carteira cashback e vale em toda a rede Bow — recarga, aspirador e os demais serviços.
            Não vira saldo nem é sacado. Há limites mensais por indicador e do programa; os
            valores podem mudar, sem mexer no que já foi creditado.
          </div>
        </div>
        <div
          className="border-t border-bow-royal/10 bg-white px-6 pt-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <Button variant="primary" size="md" fullWidth onClick={onClose}>
            Entendi
          </Button>
        </div>
      </div>
    </div>
  );
}
